import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { Project } from '@veodiagram/shared';
import { api, type ServerCapabilities } from '../services/api';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { StructuralSummary } from '../components/dashboard/StructuralSummary';
import { OperationalSummary } from '../components/dashboard/OperationalSummary';
import { SecuritySummary } from '../components/dashboard/SecuritySummary';
import { DashboardQuickNav } from '../components/dashboard/DashboardQuickNav';

type SourceType = 'github' | 'local';

export function DashboardPage() {
  const { isAuthenticated } = useAuthStore();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectRepo, setNewProjectRepo] = useState('');
  const [newProjectLocalPath, setNewProjectLocalPath] = useState('');
  const [sourceType, setSourceType] = useState<SourceType>('github');
  const [capabilities, setCapabilities] = useState<ServerCapabilities | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deletePreview, setDeletePreview] = useState<{
    diagramCount: number;
    analysisJobCount: number;
  } | null>(null);

  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch server capabilities — issue #855: handle failure gracefully.
    api.getCapabilities()
      .then((caps) => {
        setCapabilities(caps);
        setServerError(null);
      })
      .catch((err) => {
        console.error('Failed to load capabilities:', err);
        setServerError(err?.message ?? 'Cannot connect to server');
      });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadProjects();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Set default source type based on capabilities
  useEffect(() => {
    if (capabilities) {
      // If local is available but not github (local-only mode), default to local
      if (capabilities.localAnalysis && capabilities.authMode === 'local') {
        setSourceType('local');
      }
    }
  }, [capabilities]);

  const loadProjects = async () => {
    try {
      const data = await api.getProjects();
      setProjects(data);
      setServerError(null);
    } catch (error: any) {
      console.error('Failed to load projects:', error);
      if (error?.message?.includes('Cannot connect')) {
        setServerError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);

    try {
      const projectData = sourceType === 'local'
        ? {
            name: newProjectName,
            localPath: newProjectLocalPath,
            sourceType: 'local' as const,
          }
        : {
            name: newProjectName,
            repoUrl: newProjectRepo || undefined,
            sourceType: 'github' as const,
          };

      const project = await api.createProject(projectData);
      setProjects([project, ...projects]);
      setShowNewProject(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create project:', error);
      setCreateError(error instanceof Error ? error.message : 'Failed to create project');
    }
  };

  const resetForm = () => {
    setNewProjectName('');
    setNewProjectRepo('');
    setNewProjectLocalPath('');
    setCreateError(null);
  };

  const handleCancelCreate = () => {
    setShowNewProject(false);
    resetForm();
  };

  const handleDeleteClick = async (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    e.stopPropagation();
    setProjectToDelete(project);
    try {
      const preview = await api.getProjectDeletePreview(project.id);
      setDeletePreview({
        diagramCount: preview.diagramCount,
        analysisJobCount: preview.analysisJobCount,
      });
    } catch (error) {
      console.error('Failed to get delete preview:', error);
      setDeletePreview(null);
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;
    await api.deleteProject(projectToDelete.id);
    setProjects(projects.filter(p => p.id !== projectToDelete.id));
    setProjectToDelete(null);
    setDeletePreview(null);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
    setDeletePreview(null);
  };

  const showLocalOption = capabilities?.localAnalysis;
  const showGitHubOption = capabilities?.authMode !== 'local';

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        {serverError && (
          <div className="mb-4 p-4 border border-red-200 dark:border-red-800 rounded bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300">
            {serverError}
          </div>
        )}
        <p className="text-center text-muted-foreground">
          Please sign in to view your dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/*
        Issue #819 review follow-up: make the page primary-CTA-first.
        The three dashboard summaries include deliberately-synthesised
        operational and security data (no real adapters configured by
        default), and users who just want to reach their projects
        should not have to scroll past them. Quick-nav lives at the
        top and the project list is the first section below it;
        dashboard summaries follow, clearly labelled live vs preview.
      */}
      <DashboardQuickNav projectCount={projects.length} />

      <section
        id="your-projects"
        tabIndex={-1}
        className="scroll-mt-20"
      >
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Your Projects</h1>
        <div className="flex items-center gap-3">
          <Link
            to="/trash"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            title="View trash"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Trash
          </Link>
          <button
            onClick={() => setShowNewProject(true)}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            New Project
          </button>
        </div>
      </div>

      {showNewProject && (
        <div className="mb-8 p-6 border border-border rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Create New Project</h2>

          {createError && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
              {createError}
            </div>
          )}

          <form onSubmit={handleCreateProject} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1">
                Project Name
              </label>
              <input
                id="name"
                type="text"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
                placeholder="My Awesome Project"
                required
              />
            </div>

            {/* Source Type Selector - only show if both options available */}
            {showLocalOption && showGitHubOption && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Source Type
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSourceType('github')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      sourceType === 'github'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted'
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                      </svg>
                      GitHub Repository
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSourceType('local')}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                      sourceType === 'local'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-muted'
                    }`}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                      </svg>
                      Local Folder
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* GitHub Repository Input */}
            {sourceType === 'github' && showGitHubOption && (
              <div>
                <label htmlFor="repo" className="block text-sm font-medium mb-1">
                  GitHub Repository URL
                </label>
                <input
                  id="repo"
                  type="url"
                  value={newProjectRepo}
                  onChange={(e) => setNewProjectRepo(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  placeholder="https://github.com/owner/repo"
                  required
                />
              </div>
            )}

            {/* Local Folder Path Input */}
            {sourceType === 'local' && showLocalOption && (
              <div>
                <label htmlFor="localPath" className="block text-sm font-medium mb-1">
                  Local Folder Path
                </label>
                <input
                  id="localPath"
                  type="text"
                  value={newProjectLocalPath}
                  onChange={(e) => setNewProjectLocalPath(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  placeholder="/path/to/your/project"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the absolute path to your local repository folder
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
              >
                Create Project
              </button>
              <button
                type="button"
                onClick={handleCancelCreate}
                className="px-4 py-2 border border-border rounded-md text-sm font-medium hover:bg-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading projects...</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <p className="text-muted-foreground mb-4">No projects yet</p>
          <button
            onClick={() => setShowNewProject(true)}
            className="text-primary hover:underline"
          >
            Create your first project
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="relative group block p-6 border border-border rounded-lg hover:border-primary transition-colors"
            >
              <Link
                to={`/projects/${project.id}`}
                className="block"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold">{project.name}</h3>
                  {project.sourceType === 'local' ? (
                    <span className="text-xs bg-muted px-2 py-1 rounded">Local</span>
                  ) : (
                    <span className="text-xs bg-muted px-2 py-1 rounded">GitHub</span>
                  )}
                </div>
                {project.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {project.description}
                  </p>
                )}
                {project.repoUrl && (
                  <p className="text-xs text-muted-foreground truncate">
                    {project.repoUrl}
                  </p>
                )}
                {project.localPath && (
                  <p className="text-xs text-muted-foreground truncate">
                    {project.localPath}
                  </p>
                )}
              </Link>
              {/* Delete button - shown on hover */}
              <button
                onClick={(e) => handleDeleteClick(e, project)}
                className="absolute top-2 right-2 p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-all"
                title="Delete project"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      </section>

      {/*
        Dashboard summaries — structural is live, operational and
        security default to synthesised mocks until the user connects
        real observability / security adapters. Each section is
        anchored by an id so the DashboardQuickNav above can scroll
        the user into it on demand.
      */}
      <div className="mt-10 pt-6 border-t border-border">
        {/* Phase 1: Structural Summary */}
        <StructuralSummary />
        {/* Phase 2: Operational Summary */}
        <OperationalSummary />
        {/* Phase 3: Security Summary */}
        <SecuritySummary />
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Project?"
        itemName={projectToDelete?.name || ''}
        itemType="project"
        cascadeInfo={deletePreview || undefined}
      />
    </div>
  );
}
